import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, ArrowLeft, Search, Filter } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api } from '../../services/api';

export function PaymentHistoryPage() {
  const navigate = useNavigate();
  const [allActivities, setAllActivities] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'paid':
      case 'settlement':
      case 'capture':
      case 'payment_success':
      case 'subscription_activated':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pending':
      case 'payment_pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'cancelled':
      case 'cancel':
      case 'deny':
      case 'expire':
      case 'failure':
      case 'payment_cancelled':
      case 'payment_failed':
      case 'payment_expired':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'payment_created': return 'Created';
      case 'pending':
      case 'payment_pending': return 'Pending';
      case 'paid':
      case 'settlement':
      case 'capture':
      case 'payment_success': return 'Paid';
      case 'cancelled':
      case 'cancel':
      case 'payment_cancelled': return 'Cancelled';
      case 'expire':
      case 'payment_expired': return 'Expired';
      case 'deny':
      case 'failure':
      case 'payment_failed': return 'Failed';
      case 'active': return 'Active';
      case 'subscription_activated': return 'Activated';
      default: return status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : 'Unknown';
    }
  };

  const getPlanInfo = (activity: any) => {
    if (activity.metadata?.plan_name) return activity.metadata.plan_name;
    if (activity.subscriptions?.plan_name) return activity.subscriptions.plan_name;
    if (activity.description) {
      if (activity.description.includes('Plus')) return 'Plus';
      if (activity.description.includes('Premium')) return 'Premium';
      if (activity.description.includes('Pro')) return 'Pro';
      if (activity.description.includes('Free Trial')) return 'Free Trial';
    }
    if (activity.amount) {
      if (activity.amount === 35000) return 'Plus';
      if (activity.amount === 89000) return 'Premium';
      if (activity.amount === 165000) return 'Pro';
      if (activity.amount === 0) return 'Free Trial';
    }
    return '-';
  };

  const getDuration = (activity: any) => {
    if (activity.metadata?.plan_duration) {
      const duration = activity.metadata.plan_duration;
      if (duration === 0.25) return '1 Week';
      if (duration === 1) return '1 Month';
      if (duration === 3) return '3 Months';
      if (duration === 6) return '6 Months';
      return `${duration} Month${duration > 1 ? 's' : ''}`;
    }
    if (activity.subscriptions?.plan_duration) {
      const duration = activity.subscriptions.plan_duration;
      if (duration === 0.25) return '1 Week';
      if (duration === 1) return '1 Month';
      if (duration === 3) return '3 Months';
      if (duration === 6) return '6 Months';
      return `${duration} Month${duration > 1 ? 's' : ''}`;
    }
    if (activity.amount) {
      if (activity.amount === 35000) return '1 Month';
      if (activity.amount === 89000) return '3 Months';
      if (activity.amount === 165000) return '6 Months';
      if (activity.amount === 0) return '1 Week';
    }
    return '-';
  };

  const formatPaymentDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodDisplayName = (paymentMethod: string) => {
    if (!paymentMethod || paymentMethod === 'snap') return '-';
    return paymentMethod;
  };

  const loadPaymentActivity = async () => {
    try {
      if (allActivities.length === 0) setLoading(true);
      const response = await api.getPaymentActivity({ offset: 0, limit: 1000 });
      if (response.success) {
        setAllActivities(response.data.activities || []);
      }
    } catch (error) {
      console.error('Failed to load payment activity:', error);
    } finally {
      setLoading(false);
    }
  };

  // Removed blind 30 second polling to save backend resources. 
  // Data is fetched once when the page loads.
  useEffect(() => {
    loadPaymentActivity();
  }, []);

  const { filteredActivities, paginatedActivities, totalPages } = useMemo(() => {
    let filtered = allActivities;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => {
        const status = a.status_to || a.activity_type;
        return getStatusText(status).toLowerCase() === statusFilter.toLowerCase();
      });
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(a => {
        const planName = getPlanInfo(a).toLowerCase();
        const amount = a.amount ? a.amount.toString() : '';
        const method = getPaymentMethodDisplayName(a.payment_method).toLowerCase();
        const date = formatPaymentDate(a.created_at).toLowerCase();
        return planName.includes(lowerSearch) || amount.includes(lowerSearch) || method.includes(lowerSearch) || date.includes(lowerSearch);
      });
    }

    const pages = Math.ceil(filtered.length / 10) || 1;
    const validCurrentPage = Math.min(currentPage, pages) || 1;
    const paginated = filtered.slice((validCurrentPage - 1) * 10, validCurrentPage * 10);

    return { filteredActivities: filtered, paginatedActivities: paginated, totalPages: pages };
  }, [allActivities, statusFilter, searchTerm, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && allActivities.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] flex flex-col p-1 lg:p-1 overflow-y-auto overflow-x-hidden">
      <Card className="flex-1">
        <CardHeader className="py-4 px-6 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/subscription')} className="mr-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <CardTitle className="text-xl">Payment History</CardTitle>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search plan, date, method..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 bg-background/50"
                />
              </div>
              <div className="w-full sm:w-40 flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
                <Select 
                  value={statusFilter} 
                  onValueChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="created">Created</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 py-4">
          {filteredActivities.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">No</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Payment Method</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Plan</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedActivities.map((activity, index) => (
                      <tr key={activity.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {((currentPage - 1) * 10) + index + 1}
                        </td>
                        <td className="py-3 px-4 text-sm whitespace-nowrap">
                          {formatPaymentDate(activity.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant="secondary"
                            className={`text-xs whitespace-nowrap ${getStatusColor(activity.status_to || activity.activity_type)}`}
                          >
                            {getStatusText(activity.status_to || activity.activity_type)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium whitespace-nowrap">
                          {activity.amount ? `Rp ${activity.amount.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">
                          {getPaymentMethodDisplayName(activity.payment_method)}
                        </td>
                        <td className="py-3 px-4 text-sm whitespace-nowrap">
                          {getPlanInfo(activity)}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">
                          {getDuration(activity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                  <div className="text-sm text-muted-foreground hidden sm:block">
                    Showing {((Math.min(currentPage, totalPages) - 1) * 10) + 1} to {Math.min(Math.min(currentPage, totalPages) * 10, filteredActivities.length)} of {filteredActivities.length} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm font-medium px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              Belum ada riwayat transaksi pembayaran.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
